// The Chat | Agents bar inside AI Chat. MedStudentRightPanel renders this one node both
// docked and in the pop-out window, so the bar appears in both. Both panes stay mounted (the
// inactive one hidden) so switching never resets the chat; the choice survives pop-out/dock
// remounts through localStorage.
//
// It is a tab list: each tab controls its pane (a tab panel the tab labels), only the selected
// tab is in the tab order, and the arrow keys (Home and End too) move between the tabs.
import React, { useEffect, useId, useRef, useState } from 'react';
import { canAccessComponent } from '../../../config/roles';

const STORAGE_KEY = 'medstudent-ai-subtab';
const TABS = ['chat', 'agents'];

function readSubtab() {
  try { return localStorage.getItem(STORAGE_KEY) === 'agents' ? 'agents' : 'chat'; } catch { return 'chat'; }
}

// The tab to go to for a key, or null when the key is not one of the tab keys.
function tabForKey(key, from) {
  const at = TABS.indexOf(from);
  switch (key) {
    case 'ArrowRight': return TABS[(at + 1) % TABS.length];
    case 'ArrowLeft': return TABS[(at + TABS.length - 1) % TABS.length];
    case 'Home': return TABS[0];
    case 'End': return TABS[TABS.length - 1];
    default: return null;
  }
}

const CHIP = 'text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors inline-flex items-center gap-1.5';
const ACTIVE = 'bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 ring-1 ring-blue-300 dark:ring-blue-500/40';
const IDLE = 'bg-slate-100 dark:bg-slate-800/60 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700/60';

export default function AIChatPane({ userRole, unseen = 0, chat, agents }) {
  const allowed = canAccessComponent(userRole, 'clinicPage.agentsTab');
  const [tab, setTab] = useState(readSubtab);
  const prefix = useId();
  const buttons = useRef({});
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, tab); } catch { /* private mode */ }
  }, [tab]);
  if (!allowed) return chat;

  const tabId = (key) => `${prefix}-tab-${key}`;
  const panelId = (key) => `${prefix}-panel-${key}`;

  const onKeyDown = (event, from) => {
    const next = tabForKey(event.key, from);
    if (next === null) return;
    event.preventDefault();
    setTab(next);
    if (buttons.current[next]) buttons.current[next].focus();
  };

  // The icons (speech balloon, robot face) are code-point escapes so the file stays ASCII; they
  // are decoration, hidden from screen readers.
  const chip = (key, icon, label, badge = 0) => (
    <button key={key} type="button" role="tab" id={tabId(key)} aria-controls={panelId(key)}
            aria-selected={tab === key} tabIndex={tab === key ? 0 : -1}
            ref={(el) => { buttons.current[key] = el; }}
            onClick={() => setTab(key)} onKeyDown={(event) => onKeyDown(event, key)}
            className={[CHIP, tab === key ? ACTIVE : IDLE].join(' ')}>
      <span aria-hidden="true">{icon}</span>
      {label}
      {badge > 0 && (
        <span role="img" aria-label={`${badge} agent ${badge === 1 ? 'update' : 'updates'}`}
              className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-rose-600 dark:bg-rose-500 text-white">
          {badge}
        </span>
      )}
    </button>
  );

  const panel = (key, content) => (
    <div role="tabpanel" id={panelId(key)} aria-labelledby={tabId(key)}
         className={tab === key ? 'flex-1 flex flex-col' : 'hidden'} style={{ minHeight: 0 }}>
      {content}
    </div>
  );

  return (
    <div className="flex-1 flex flex-col" style={{ minHeight: 0 }}>
      <div role="tablist" aria-label="AI Chat views"
           className="flex gap-1.5 px-3 pt-2.5 pb-2 border-b border-slate-200 dark:border-slate-800/60">
        {chip('chat', '\u{1F4AC}', 'Chat')}
        {chip('agents', '\u{1F916}', 'Agents', unseen)}
      </div>
      {panel('chat', chat)}
      {panel('agents', agents)}
    </div>
  );
}
